/**
 * Standardized Holiday Options - Single Source of Truth
 *
 * This file defines all valid holiday values across the application.
 * Used by forms, filters, search, and database validation.
 *
 * @see Database: displays.holidays (text[]), sequences.holiday_type (text)
 * @see Typesense: displays.holiday_type (string), sequences_v2.holiday_type (string)
 * @see Migration: supabase/migrations/20250930000001_standardize_holidays.sql (displays)
 * @see Migration: supabase/migrations/[timestamp]_standardize_sequence_holidays.sql (sequences)
 */

export const HOLIDAY_OPTIONS = [
  // Primary Holidays (User's top 5 requirements)
  {
    value: 'Christmas',
    label: 'Christmas',
    icon: '🎄',
    primary: true,
    sortOrder: 1
  },
  {
    value: 'Halloween',
    label: 'Halloween',
    icon: '🎃',
    primary: true,
    sortOrder: 2
  },
  {
    value: '4th of July',
    label: '4th of July',
    icon: '🎆',
    primary: true,
    sortOrder: 3
  },
  {
    value: 'Valentines Day',
    label: "Valentine's Day",
    icon: '❤️',
    primary: true,
    sortOrder: 4
  },
  {
    value: 'New Year',
    label: 'New Year',
    icon: '🎊',
    primary: true,
    sortOrder: 5
  },

  // Secondary Holidays (Additional popular options)
  {
    value: 'Easter',
    label: 'Easter',
    icon: '🐰',
    primary: false,
    sortOrder: 6
  },
  {
    value: 'Thanksgiving',
    label: 'Thanksgiving',
    icon: '🦃',
    primary: false,
    sortOrder: 7
  },
  {
    value: 'Hanukkah',
    label: 'Hanukkah',
    icon: '🕎',
    primary: false,
    sortOrder: 8
  },
  {
    value: 'Other',
    label: 'Other',
    icon: '✨',
    primary: false,
    sortOrder: 9
  },
] as const;

/**
 * TypeScript type for valid holiday values
 * Ensures type safety across the application
 */
export type HolidayValue = typeof HOLIDAY_OPTIONS[number]['value'];

/**
 * Helper type for holiday option object
 */
export type HolidayOption = typeof HOLIDAY_OPTIONS[number];

/**
 * Primary holidays (top 5 user requirements)
 */
export const PRIMARY_HOLIDAYS = HOLIDAY_OPTIONS.filter(h => h.primary);

/**
 * Secondary holidays (additional options)
 */
export const SECONDARY_HOLIDAYS = HOLIDAY_OPTIONS.filter(h => !h.primary);

/**
 * Array of all valid holiday values for validation
 */
export const VALID_HOLIDAY_VALUES: readonly HolidayValue[] = HOLIDAY_OPTIONS.map(h => h.value);

/**
 * Map for quick lookup by value
 */
export const HOLIDAY_MAP = new Map(
  HOLIDAY_OPTIONS.map(h => [h.value, h])
);

/**
 * Get holiday option by value
 */
export function getHolidayOption(value: string): HolidayOption | undefined {
  return HOLIDAY_MAP.get(value);
}

/**
 * Validate if a value is a valid holiday
 */
export function isValidHoliday(value: string): value is HolidayValue {
  return HOLIDAY_MAP.has(value);
}

/**
 * Normalize holiday value (for migration from old inconsistent data)
 * Handles case-insensitive matching and common variations
 */
export function normalizeHolidayValue(value: string): HolidayValue | null {
  const normalized = value.trim();

  // Direct match (case-sensitive)
  if (isValidHoliday(normalized)) {
    return normalized;
  }

  // Case-insensitive match
  const lowerValue = normalized.toLowerCase();
  const match = HOLIDAY_OPTIONS.find(
    h => h.value.toLowerCase() === lowerValue || h.label.toLowerCase() === lowerValue
  );

  return match ? match.value : null;
}

/**
 * Migrate old holiday values to new standardized values
 * Used for data cleanup and backward compatibility
 */
export function migrateHolidayValues(oldValues: string[]): HolidayValue[] {
  return oldValues
    .map(normalizeHolidayValue)
    .filter((v): v is HolidayValue => v !== null);
}

/**
 * LEGACY SEQUENCE VALUE MAPPING
 * Maps old camelCase sequence values to new standardized values
 *
 * @deprecated This mapping exists for backward compatibility with existing sequence data.
 * New sequences should use standardized values directly.
 */
const LEGACY_SEQUENCE_VALUE_MAP: Record<string, HolidayValue> = {
  'Christmas': 'Christmas',
  'Halloween': 'Halloween',
  'FourthOfJuly': '4th of July',
  'Easter': 'Easter',
  'Thanksgiving': 'Thanksgiving',
  'Hanukkah': 'Hanukkah',
  'NewYear': 'New Year',
  'ValentinesDay': 'Valentines Day',
  'Other': 'Other',
};

/**
 * Normalize legacy sequence holiday values to standardized format
 * Handles camelCase variants from old sequence data
 *
 * @param legacyValue - Old camelCase value from sequences table
 * @returns Standardized holiday value
 *
 * @example
 * normalizeSequenceHolidayValue('FourthOfJuly') // Returns '4th of July'
 * normalizeSequenceHolidayValue('NewYear') // Returns 'New Year'
 * normalizeSequenceHolidayValue('Christmas') // Returns 'Christmas' (already standard)
 */
export function normalizeSequenceHolidayValue(legacyValue: string): HolidayValue | null {
  // First try direct legacy mapping
  if (legacyValue in LEGACY_SEQUENCE_VALUE_MAP) {
    return LEGACY_SEQUENCE_VALUE_MAP[legacyValue];
  }

  // Fall back to general normalization
  return normalizeHolidayValue(legacyValue);
}

/**
 * Get legacy sequence value from standardized value
 * Used when interfacing with old code that expects camelCase
 *
 * @deprecated Only use when absolutely necessary for backward compatibility
 */
export function getLegacySequenceValue(standardValue: HolidayValue): string {
  const reverseMap: Record<HolidayValue, string> = {
    'Christmas': 'Christmas',
    'Halloween': 'Halloween',
    '4th of July': 'FourthOfJuly',
    'Easter': 'Easter',
    'Thanksgiving': 'Thanksgiving',
    'Hanukkah': 'Hanukkah',
    'New Year': 'NewYear',
    'Valentines Day': 'ValentinesDay',
    'Other': 'Other',
  };

  return reverseMap[standardValue] || standardValue;
}
